<template>
  <div class="makeup-capacity-demo is-fullwidth">
    <h2 class="title is-4 mb-2">Makeup Weekly Capacity (Dummy Data)</h2><br />
    

    <div class="chart-wrap">
      <div class="chart-head">
        <span class="legend-item"><span class="legend-box"></span> Within capacity</span>
        <span class="legend-item"><span class="legend-box over"></span> Over capacity</span>
      </div>

      <div class="rows-wrap" aria-label="Weekly makeup garments horizontal bar chart">
        <div class="chart-row" v-for="week in weeklySummary" :key="week.week" :title="weekTooltip(week)">
          <div class="week-name">{{ week.week }}</div>

          <div class="track-wrap">
            <div class="bar-track">
              <div class="capacity-marker" :style="{ left: capacityMarkerPercent + '%' }"></div>
              <div
                class="bar-fill"
                :class="{ over: week.totalGarments > capacityLimit }"
                :style="{ width: week.barWidthPercent + '%' }"
              ></div>
            </div>
            
          </div>

          <div class="week-meta">
            <div class="meta-total">{{ formatNumber(week.totalGarments) }}</div>
            <div class="meta-orders">{{ week.orders.length }} orders</div>
          </div>
        </div>
      </div>
    </div>

    <div class="table-wrap mt-5">
      <table class="table is-fullwidth is-striped is-hoverable is-size-7">
        <thead>
          <tr>
            <th>Week</th>
            <th>Orders</th>
            <th>Total Garments</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="week in weeklySummary" :key="week.week + '-row'">
            <td>{{ week.week }}</td>
            <td>{{ week.orders.length }}</td>
            <td>{{ formatNumber(week.totalGarments) }}</td>
            <td>
              <span
                class="status-tag"
                :class="week.totalGarments > capacityLimit ? 'status-over' : 'status-ok'"
              >
                {{ week.totalGarments > capacityLimit ? 'Over capacity' : 'Within capacity' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      capacityLimit: 17000,
      weeklyOrders: [
        {
          week: 'Week 1',
          orders: [2600, 2300, 2800, 2100, 1900, 2400]
        },
        {
          week: 'Week 2',
          orders: [4200, 3500, 2900, 2700, 1900, 2200]
        },
        {
          week: 'Week 3',
          orders: [1800, 2200, 1400, 2600, 2000, 1700]
        },
        {
          week: 'Week 4',
          orders: [4500, 3900, 3600, 2500, 2100, 1800]
        },
        {
          week: 'Week 5',
          orders: [2200, 2600, 3100, 1500, 1400, 2100, 1600]
        },
        {
          week: 'Week 6',
          orders: [3800, 4100, 3600, 2600, 2200, 1700]
        },
        {
          week: 'Week 7',
          orders: [2000, 2400, 2600, 2800, 2100, 1700, 1400]
        },
        {
          week: 'Week 8',
          orders: [3200, 3400, 2900, 2500, 2100, 1900]
        },
        {
          week: 'Week 9',
          orders: [1900, 2300, 2800, 2100, 1800, 1700]
        },
        {
          week: 'Week 10',
          orders: [4700, 4200, 3000, 2700, 2200, 1900]
        }
      ]
    };
  },
  computed: {
    weeklySummary() {
      return this.weeklyOrders.map((week) => {
        const totalGarments = week.orders.reduce((sum, garments) => sum + garments, 0);

        return {
          ...week,
          totalGarments,
          barWidthPercent: (totalGarments / this.chartMax) * 100
        };
      });
    },
    chartMax() {
      const maxWeeklyTotal = Math.max(
        ...this.weeklyOrders.map((week) => week.orders.reduce((sum, garments) => sum + garments, 0)),
        this.capacityLimit
      );

      return Math.ceil(maxWeeklyTotal * 1.1);
    },
    capacityMarkerPercent() {
      return (this.capacityLimit / this.chartMax) * 100;
    }
  },
  methods: {
    formatNumber(value) {
      return new Intl.NumberFormat('en-GB').format(value);
    },
    formatCompact(value) {
      return new Intl.NumberFormat('en-GB', {
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(value);
    },
    weekTooltip(week) {
      return `${week.week}: ${week.orders.length} orders, ${this.formatNumber(week.totalGarments)} garments`;
    }
  }
};
</script>

<style scoped>
.makeup-capacity-demo {
  padding: 1rem;
}

.chart-wrap {
  border: 1px solid #d8dde6;
  border-radius: 10px;
  background: linear-gradient(180deg, #fafbfd 0%, #f2f5fa 100%);
  padding: 1rem;
}

.chart-head {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.8rem;
  font-size: 0.75rem;
  color: #334155;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.legend-box {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: #2563eb;
}

.legend-box.over {
  background: #dc2626;
}

.rows-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.chart-row {
  display: grid;
  grid-template-columns: 96px 1fr 120px;
  gap: 0.75rem;
  align-items: center;
}

.week-name {
  font-size: 0.75rem;
  font-weight: 600;
  color: #334155;
}

.track-wrap {
  width: 100%;
}

.bar-track {
  position: relative;
  height: 20px;
  border-radius: 999px;
  background: #dbe4f1;
  overflow: hidden;
}

.capacity-marker {
  position: absolute;
  top: -2px;
  bottom: -2px;
  width: 2px;
  background: #be123c;
  z-index: 2;
}

.bar-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #0ea5e9 0%, #2563eb 100%);
}

.bar-fill.over {
  background: linear-gradient(90deg, #ef4444 0%, #b91c1c 100%);
}

.track-scale {
  margin-top: 0.25rem;
  display: flex;
  justify-content: space-between;
  font-size: 0.62rem;
  color: #64748b;
}

.capacity-scale {
  color: #9f1239;
  font-weight: 700;
}

.week-meta {
  text-align: right;
}

.meta-total {
  font-size: 0.76rem;
  font-weight: 700;
  color: #1e293b;
}

.meta-orders {
  font-size: 0.65rem;
  color: #64748b;
}

.status-tag {
  display: inline-block;
  border-radius: 999px;
  padding: 0.12rem 0.55rem;
  font-size: 0.7rem;
  font-weight: 700;
}

.status-ok {
  color: #166534;
  background-color: #dcfce7;
}

.status-over {
  color: #991b1b;
  background-color: #fee2e2;
}

@media (max-width: 1024px) {
  .chart-row {
    grid-template-columns: 80px 1fr 104px;
  }
}

@media (max-width: 768px) {
  .chart-row {
    grid-template-columns: 1fr;
    gap: 0.35rem;
    padding: 0.55rem 0.45rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
  }

  .week-meta {
    text-align: left;
  }
}
</style>

